import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, X, Clock, RefreshCw, ChevronDown, ChevronUp, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  if (status === "approved") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-moss/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-moss-dark"><CheckCircle2 className="h-3 w-3" /> Approved</span>;
  }
  if (status === "rejected") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-[0.65rem] font-semibold text-rose-700"><X className="h-3 w-3" /> Rejected</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-ochre/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-ochre"><Clock className="h-3 w-3" /> Pending</span>;
}

function KeyVal({ k, v }) {
  if (v === null || v === undefined || v === "" || v === 0) return null;
  return <div className="flex justify-between gap-3"><dt className="text-[0.65rem] uppercase tracking-wide text-ink-700">{k}</dt><dd className="text-xs font-semibold text-ink-900">{String(v)}</dd></div>;
}

export default function SetupReviews() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIds, setOpenIds] = useState(new Set());
  const [acting, setActing] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/setup-submissions`);
      setRows(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = (id) => setOpenIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const approve = async (id) => {
    if (!window.confirm("Approve this submission? The school's profile, vendors and cook data will be overwritten with the submitted values.")) return;
    setActing((s) => ({ ...s, [id]: "approve" }));
    try {
      await axios.post(`${API}/setup-submissions/${id}/approve`);
      toast.success("Approved — school data updated.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setActing((s) => ({ ...s, [id]: null }));
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this submission? The school's live data will NOT be changed.")) return;
    setActing((s) => ({ ...s, [id]: "reject" }));
    try {
      await axios.post(`${API}/setup-submissions/${id}/reject`);
      toast.success("Rejected.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setActing((s) => ({ ...s, [id]: null }));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/setup-submissions/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const pending = rows.filter((r) => r.status === "pending");

  return (
    <section className="space-y-4" data-testid="admin-setup-reviews-tab">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold text-ink-900">Setup Reviews</h2>
          <p className="text-xs text-ink-700">{pending.length} pending · {rows.length} total</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="setup-reviews-refresh-btn">
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
        </Button>
      </header>

      {loading ? (
        <p className="rounded-xl bg-sand-50 px-3 py-2 text-xs text-ink-700">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border bg-white/50 p-8 text-center">
          <ShieldCheck className="mx-auto h-8 w-8 text-ink-500" />
          <p className="mt-2 text-sm text-ink-700">No setup submissions yet.</p>
          <p className="mt-1 text-xs text-ink-700">Share a setup link from the Schools tab and submissions will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((s) => {
            const open = openIds.has(s.id);
            const isActing = !!acting[s.id];
            return (
              <div key={s.id} className="rounded-2xl border border-border bg-white shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid={`setup-review-${s.id}`}>
                <button type="button" onClick={() => toggle(s.id)} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left">
                  <div className="min-w-0 flex-1">
                    <p className="font-heading text-base font-bold text-ink-900">{s.schoolName || "Unknown school"}</p>
                    <p className="text-[0.7rem] text-ink-700">UDISE {s.udiseCode || "—"} · {new Date(s.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={s.status} />
                    {open ? <ChevronUp className="h-4 w-4 text-clay" /> : <ChevronDown className="h-4 w-4 text-clay" />}
                  </div>
                </button>

                {open && (
                  <div className="space-y-3 border-t border-border px-5 py-4" data-testid={`setup-review-${s.id}-body`}>
                    <div className="rounded-xl border border-border bg-sand-50 p-3">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700">School Profile</p>
                      <dl className="mt-2 space-y-1">
                        <KeyVal k="School Name" v={s.profile?.schoolName} />
                        <KeyVal k="School Name (Odia)" v={s.profile?.schoolNameOdia} />
                        <KeyVal k="Category" v={s.profile?.category} />
                        <KeyVal k="District" v={s.profile?.district} />
                        <KeyVal k="Block" v={s.profile?.block} />
                        <KeyVal k="Village" v={s.profile?.village} />
                        <KeyVal k="Post" v={s.profile?.post} />
                        <KeyVal k="Thana" v={s.profile?.thana} />
                        <KeyVal k="PIN" v={s.profile?.pincode} />
                        <KeyVal k="Bal Vatika" v={s.profile?.balVatikaEnrollment} />
                        <KeyVal k="Primary" v={s.profile?.primaryEnrollment} />
                        <KeyVal k="Upper Primary" v={s.profile?.upperPrimaryEnrollment} />
                        <KeyVal k="High School" v={s.profile?.highSchoolEnrollment} />
                      </dl>
                    </div>

                    <div className="rounded-xl border border-border bg-sand-50 p-3">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700">Vendors ({(s.vendors || []).length})</p>
                      {(s.vendors || []).length === 0 ? (
                        <p className="mt-1 text-xs italic text-ink-700">No vendors submitted.</p>
                      ) : (
                        <ul className="mt-2 space-y-1.5">
                          {s.vendors.map((v, i) => (
                            <li key={i} className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs">
                              <b className="text-ink-900">{v.name}</b>
                              <span className="text-ink-700"> · {v.category || "—"} · {v.village || "—"} · {v.phone || "—"}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="rounded-xl border border-border bg-sand-50 p-3">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-700">Cook-cum-Helpers ({(s.cchs || []).length})</p>
                      {(s.cchs || []).length === 0 ? (
                        <p className="mt-1 text-xs italic text-ink-700">No cooks submitted.</p>
                      ) : (
                        <ul className="mt-2 space-y-1.5">
                          {s.cchs.map((c, i) => (
                            <li key={i} className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs">
                              <b className="text-ink-900">{c.name}</b>
                              <span className="text-ink-700"> · {c.relationType === "husband" ? "Husband" : "Father"}: {c.fatherName || "—"} · {c.village || "—"} · ₹{c.monthlyHonorarium || 0}/m</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {s.status === "pending" ? (
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => reject(s.id)} disabled={isActing} data-testid={`setup-review-${s.id}-reject-btn`} className="gap-1.5 text-rose-700 hover:bg-rose-50 hover:text-rose-700">
                          <X className="h-3.5 w-3.5" /> Reject
                        </Button>
                        <Button size="sm" onClick={() => approve(s.id)} disabled={isActing} data-testid={`setup-review-${s.id}-approve-btn`} className="gap-1.5 bg-moss text-white hover:bg-moss-dark">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {acting[s.id] === "approve" ? "Applying…" : "Approve & Apply"}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[0.7rem] text-ink-700">
                          {s.status === "approved" ? "Applied " : "Rejected "}
                          {s.reviewedAt ? new Date(s.reviewedAt).toLocaleString("en-IN") : ""}
                          {s.reviewedBy ? ` by ${s.reviewedBy}` : ""}
                        </p>
                        <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`setup-review-${s.id}-delete-btn`} className="gap-1.5 text-destructive hover:bg-red-50 hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
